// Direct imports
import type { Observer } from "@babylonjs/core/Misc/observable";
import { Material } from "@babylonjs/core/Materials/material";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { SubMesh } from "@babylonjs/core/Meshes/subMesh";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { AssetContainer } from "@babylonjs/core/assetContainer";
import type { ContainerLoadOptions } from "../loaders/ResourceManager";
import { isOccluderMaterial } from "../loaders/gltf/TRACE_materials_occlusion";
import { SceneObject } from "../core/SceneObject";
import { COLLIDER_MESH_SUFFIX, SELECTION_MESH_SUFFIX } from "./boxcollider";
import { TraceBehavior, findPropertyProvider, isPropertyBinding, wholeRefName, WHOLE_REF_RE } from "./tracebehavior";
import { drivesValue, drivingHexColor, HEX_COLOR_RE } from "./bindingValues";
import { RendererData, MaterialData } from "../scenedata/behaviors/rendererdata";
import { Logger } from "../utils/Logger";
import { authoredTransparencyOf, clearAuthoredTransparency, cloneMaterial, recordAuthoredTransparency, transparencyOwnedElsewhere } from "../utils/materialClone";
import { copyFactorTextureSettings, deriveFactorTexture, type FactorChannels } from "../utils/factorTexture";
import { promoteInstanceToMesh } from "../utils/promoteInstance";
import { readAtBase } from "../core/canopy/PropertyResolver";
import { ValidationError } from "../scenedata/behaviordata";

/**
 * Outcome of a single async material load+assign. Callers use it to decide
 * whether to apply the material's authored properties afterwards:
 *   - `assigned`   — a fresh clone landed on the live mesh; apply properties.
 *   - `failed`     — an ordinary load failure (empty/absent container, clone
 *                    failure, fetch throw). The slot keeps whatever material it
 *                    had; apply authored properties over it anyway (matches the
 *                    pre-race behavior where a 404'd GLB still got its authored
 *                    color/roughness). Recoverable — a later commit retries.
 *   - `superseded` — a concurrent commit re-owned the slot (src changed) or the
 *                    mesh vanished mid-load; DO NOT apply this stale entry's
 *                    properties, they would clobber the winner.
 *   - `detached`   — the renderer detached (reload) mid-load; the caller must
 *                    stop entirely, not touch the now-cleared slot map.
 */
type MaterialLoadOutcome = 'assigned' | 'failed' | 'superseded' | 'detached';

/** Longest a posed thumbnail waits for one state material's shader before rendering anyway. */
const MATERIAL_COMPILE_TIMEOUT_MS = 2000;
/** Poll interval for that wait — about a frame. */
const MATERIAL_COMPILE_POLL_MS = 16;
/**
 * How long one export waits, in all, for material loads already in flight,
 * the same bound TraceExport gives textures: past it the bake takes the
 * meshes as they are rather than hold a save open on a load that never
 * answers. One budget per export: the exporter turns it into one deadline
 * and hands that to every object it poses.
 */
export const MATERIAL_SETTLE_TIMEOUT_MS = 10_000;

/**
 * Compile `material` for `mesh` on a temporary SubMesh (never the live one,
 * whose effect belongs to the material it shows), so a later clone with the
 * same defines finds the engine's cached effect ready. This is Babylon's
 * `forceCompilation` check with a deadline: that one polls until ready with no
 * limit, and a material that never gets there (a texture that failed to load)
 * must not stall the thumbnail queue or leave a timer running.
 */
async function compileMaterialForMesh(material: Material, mesh: AbstractMesh): Promise<void> {
    const isReady = (): boolean => {
        try {
            if (!material._storeEffectOnSubMeshes) return material.isReady(mesh);
            const probe = new SubMesh(0, 0, 0, 0, 0, mesh, undefined, false, false);
            return material.isReadyForSubMesh(mesh, probe);
        } catch {
            return true; // a probe that throws will not recover by waiting
        }
    };
    const deadline = Date.now() + MATERIAL_COMPILE_TIMEOUT_MS;
    while (!isReady() && Date.now() < deadline) {
        await new Promise(resolve => setTimeout(resolve, MATERIAL_COMPILE_POLL_MS));
    }
}

/**
 * Renderer behavior handles material application using the Renderer data format
 * Uses materials[] array instead of slots[] for cleaner material management
 */
export class Renderer extends TraceBehavior<RendererData> {
    static readonly behaviorType = "Renderer" as const;

    private _lastPickThrough = false;

    /** Hears the object's meshes change, so a mesh built after attach gets the object's policies. */
    private _meshesObserver: Observer<void> | null = null;

    /**
     * Original material names indexed by mesh index.
     * Captured during onAttach() before any material replacements are applied.
     * Used by editor utilities (MaterialPreviews) for slot detection.
     */
    private _originalMaterials: Record<number, string> = {};

    /** Container cache paths for material GLBs, for cleanup on detach */
    private _materialContainerPaths: string[] = [];

    /**
     * Material assets load with every material instantiated: a geometry-less
     * `.gltf` material has no primitive to reference it, so without the option
     * the container is empty. The option is part of the container cache key,
     * so acquire and release go through the two helpers below and always agree.
     */
    private static readonly MATERIAL_CONTAINER_OPTS: ContainerLoadOptions = { allMaterials: true };

    /** Cloned materials owned by this behavior, keyed by target mesh index. Disposed on detach. */
    private _clonedMaterials: Map<number, Material> = new Map();

    /**
     * GLB src currently applied per slot. This is the diff baseline for
     * hot-swapping in applyCommit() — authored data can hold a property
     * binding (`src: "$material"`), so comparing against authored `src`
     * would treat every re-resolution as a src change and reload the GLB.
     */
    private _loadedSrcBySlot: Map<number, string> = new Map();

    /**
     * Material work nothing awaits: a commit a binding change fired (a style
     * landing on the provider), the late-resolved sources onAttach starts, and
     * the factor-texture readbacks. A bake reads the materials on the meshes,
     * so it waits for these first.
     */
    private readonly _materialWork = new Set<Promise<unknown>>();

    /** Original transparency modes per mesh index, captured before any opacity changes. */
    private _originalTransparencyModes: Map<number, number | null> = new Map();
    /**
     * Original alpha per mesh index, captured with the mode. A restore hands
     * this back rather than 1: GLB-authored glass carries an alpha below 1 in
     * its material, and a slot must return to that, not to opaque, whether it
     * wears a clone another override keeps or the shared glTF material.
     */
    private _originalAlphas: Map<number, number> = new Map();
    /**
     * The tiling a texture carried (the glTF loader's KHR_texture_transform)
     * before a `texture_transform` first overrode it, so withdrawing the
     * override gives that back instead of 1,1.
     */
    private _authoredUvScale: WeakMap<Texture, readonly [number, number]> = new WeakMap();

    /**
     * Slots whose alpha a `#RRGGBBAA` baseColorFactor owns, with that alpha.
     * Ownership is what tells the two transparency passes apart: the colour
     * pass and the opacity pass each restore only what they changed, because
     * "was opacity authored?" cannot be asked — every loaded Renderer carries
     * the schema default `opacity: 1` (SceneCreation builds behaviors with
     * createDefaultData), and reading that as authored made the opacity pass
     * clobber every colour-carried alpha in a real scene.
     */
    private _colorAlpha: Map<number, number> = new Map();
    /**
     * Slots the opacity pass made transparent — the only ones it restores. The
     * pass is one rule for the whole-object `opacity` and a slot's own
     * (`MaterialData.opacity`): both are factors of the same alpha.
     */
    private _opacitySlots: Set<number> = new Set();

    /** Authored albedo per MATERIAL (not per slot): slots share materials in
     *  real GLBs, and a per-slot capture would take an already-tinted albedo
     *  as the second slot's base (tint²) and make restore order-dependent. */
    private _originalAlbedo: Map<Material, Color3> = new Map();

    /** Authored PBR factors per MATERIAL, captured just before this behavior
     *  first overrides them, so a later clear restores the real authored value.
     *  Keyed by material rather than slot for the same reason as
     *  _originalAlbedo. Values stay nullable: Babylon reads `null` as "defer to
     *  the metallicRoughness texture", which is not the same as 0. */
    private _originalPbrFactors: Map<Material, { roughness: number | null; metallic: number | null; emissive: Color3 }> = new Map();

    /** Per MATERIAL while a factor overrides it: the metallic-roughness texture
     *  it authored and the copy it wears instead (see _applyFactorTexture). */
    private _factorTextures: Map<Material, {
        mask: string;
        authored: Texture;
        worn: Texture;
        onDispose: Observer<Material> | null;
    }> = new Map();

    /** Copies by source GPU texture and overridden channels; null while the
     *  readback runs, and for good when the source needs no copy or the
     *  readback fails (that source keeps multiplying until reload). */
    private _derivedFactorTextures: Map<string, Texture | null> = new Map();

    /** Each copy's source, by the copy's GPU texture, held as this behavior's
     *  own texture so it outlives the material it was read from. */
    private _factorCopySources: Map<number, Texture> = new Map();

    /** Sources still loading when a factor first applied, each with the
     *  observer that applies the factors again once it has loaded. */
    private _factorLoadWaits: Map<Texture, Observer<Texture>> = new Map();

    /**
     * Authored albedo per MATERIAL, captured just before this behavior first
     * overrides `baseColorFactor` — the color analog of _originalPbrFactors,
     * and distinct from _originalAlbedo, which the tint machinery owns and
     * deliberately drops whenever the base color is rewritten.
     *
     * It exists because "the authored color" is not a property of the mesh
     * slot. A slot carrying a replacement GLB authored *the replacement's*
     * albedo; restoring the pre-replacement mesh material's color there (which
     * is all `_originalMaterials[slot]` can offer) hands back a color that
     * material never had. Capturing per material, lazily, is what makes
     * "restore what this material authored" mean the same thing for a native
     * slot and a replaced one.
     */
    private _authoredAlbedo: Map<Material, Color3> = new Map();

    /**
     * Pinned mesh reference map: slotIndex → AbstractMesh.
     * Captured once in captureOriginalMaterials() (during onAttach(), after
     * asset behaviors have loaded) and maintained through InstancedMesh
     * promotions via ensureMaterialAssignable().
     * All slot-indexed operations use this map instead of re-querying the volatile scene graph.
     */
    private _meshSlots: Map<number, AbstractMesh> = new Map();

    /**
     * Prefetched material containers, keyed by material data index.
     * Populated during load() (I/O only), consumed during onAttach()
     * to assign materials to meshes synchronously.
     */
    private _prefetchedContainers: Map<number, AssetContainer> = new Map();

    /**
     * Accepted tint formats: #RRGGBB or #RRGGBBAA. A trailing alpha byte is
     * accepted but IGNORED — tint only multiplies RGB; opacity owns alpha.
     * Checked explicitly because Color4.FromHexString does not throw on bad
     * input (Babylon 9.11 returns Color4(0,0,0,0)), which would silently
     * tint the whole model black. See behaviors/bindingValues.ts.
     */
    private static readonly TINT_HEX_RE = HEX_COLOR_RE;

    /** List of PBR texture properties that should have UV scale applied */
    private static readonly TEXTURE_PROPERTIES = [
        'albedoTexture',      // Base color / diffuse
        'bumpTexture',        // Normal map
        'metallicTexture',    // Metallic
        'roughnessTexture',   // Roughness (if separate from metallic)
        'ambientTexture',     // Ambient occlusion
        'emissiveTexture',    // Emissive
        'opacityTexture',     // Opacity/Alpha
        'reflectionTexture',  // Reflection (less common to scale)
        'microSurfaceTexture' // Microsurface
    ];

    constructor(sceneObject: SceneObject, data: RendererData) {
        super(sceneObject, data);

        // Validate initial data
        const errors = this.validate(data);
        if (errors.length > 0) {
            Logger.warn(`[Renderer] Invalid initial data for ${sceneObject.name}:`, errors);
        }
    }

    /**
     * Load required resources (replacement material GLBs).
     * Only performs I/O — does NOT access meshes or apply materials.
     * Mesh-dependent work is deferred to onAttach() where meshes are guaranteed
     * to exist (all behavior load() calls have resolved before attach).
     */
    public async load(): Promise<void> {
        Logger.debug(`[Renderer] Loading renderer component for ${this.sceneObject.name}`);

        const materials = this._resolved.materials || [];
        Logger.debug(`[Renderer] Number of materials: ${materials.length}`);

        // Release any previously prefetched containers (re-load scenario)
        for (const path of this._materialContainerPaths) {
            this._releaseMaterialContainer(path);
        }
        this._materialContainerPaths = [];
        this._prefetchedContainers.clear();

        // Prefetch material GLBs into containers — I/O only, no mesh access
        await this.prefetchMaterialContainers();
    }

    /**
     * Called after all behaviors' load() calls have resolved.
     * Meshes are guaranteed to exist. Captures original materials,
     * assigns prefetched replacement materials, applies properties.
     */
    protected onAttach(): void {
        Logger.debug(`[Renderer] onAttach for ${this.sceneObject.name}`);

        this.sceneObject.onOwnedMeshesChangedObservable.remove(this._meshesObserver);
        this._meshesObserver = this.sceneObject.onOwnedMeshesChangedObservable.add(() => this.refreshMeshes());

        // On re-attach (reload scenario), restore original materials first
        this._releaseFactorTextures();
        for (const slotIndex of [...this._clonedMaterials.keys()]) {
            this.restoreOriginalMaterial(slotIndex);
        }
        this._clonedMaterials.clear();
        this._loadedSrcBySlot.clear();
        this._originalAlbedo.clear(); // stale after reload — tint re-captures from fresh materials
        this._originalPbrFactors.clear(); // ditto: re-captured from the fresh materials on first override
        this._authoredAlbedo.clear(); // ditto

        // Capture original materials now that meshes exist
        this.captureOriginalMaterials();

        // Assign prefetched replacement materials to their target meshes
        this.assignPrefetchedMaterials();

        // Apply color/UV property overrides
        this.applyAllMaterialProperties();

        this._syncModelPins();
        this._applyCastShadows();
        this._applyOpacity();
        this._applyTint();

        this._lastPickThrough = this._resolved.pickThrough === true;
        if (this._lastPickThrough) this.sceneObject.traceScene.refreshMeshPickability?.(this.sceneObject);

        // Load any material `src` that resolved only AFTER load()'s prefetch —
        // a bound src (`"$structureSrc"`) whose providing Properties/sheet was
        // not ready during load(). Prefetch skipped it, so assignPrefetched
        // above had no container to assign and the slot kept Babylon's default
        // material. This is the "styled material goes flat on reload" bug.
        void this._trackMaterialWork(this._loadResolvedUnfetchedSources());
    }

    private _trackMaterialWork<T>(work: Promise<T>): Promise<T> {
        this._materialWork.add(work);
        const done = (): void => { this._materialWork.delete(work); };
        work.then(done, done);
        return work;
    }

    /** Wait for {@link _materialWork}, including work it starts, until `deadline` (epoch ms). */
    private async _settleMaterialWork(deadline: number): Promise<void> {
        while (this._materialWork.size > 0) {
            const left = deadline - Date.now();
            if (left <= 0) {
                Logger.warn(`[Renderer] ${this.sceneObject.name}: material loads still running at the export's deadline; baking the materials on the meshes now`);
                return;
            }
            let timer: ReturnType<typeof setTimeout> | undefined;
            await Promise.race([
                Promise.allSettled([...this._materialWork]),
                new Promise(resolve => { timer = setTimeout(resolve, left); }),
            ]);
            clearTimeout(timer);
        }
    }

    /**
     * Fetch and assign material GLB sources that resolved after prefetch.
     *
     * Prefetch (during load()) skips a material whose `src` is an unresolved
     * binding — the provider/sheet may load later. By onAttach the binding is
     * usually resolved to a URL, but that URL was never fetched, so the slot
     * would keep its default material (losing the applied style's roughness /
     * metallic / textures). Idempotent: `_loadedSrcBySlot` skips slots the
     * prefetch or a prior commit already loaded, so only genuinely-missing
     * sources are fetched.
     */
    private async _loadResolvedUnfetchedSources(): Promise<void> {
        const materials = this._materialsBySlotLastWins(this._resolved.materials || []);
        // Slots are distinct after last-wins, so their loads run side by side.
        const applied = await Promise.all(materials.map(async (mat, index) => {
            const targetIndex = mat.targetMaterialIndex ?? 0;
            if (!drivesValue(mat.src)) return false;
            if (mat.src === this._loadedSrcBySlot.get(targetIndex)) return false;
            // Structured exactly like applyCommit's slot load so both load callers
            // behave identically: loadAndAssignMaterial owns its fetch failures
            // (returns 'failed'), and the default 'failed' also covers any
            // unexpected non-fetch throw — either way authored props still apply.
            let outcome: MaterialLoadOutcome = 'failed';
            try {
                outcome = await this.loadAndAssignMaterial(index, mat);
            } catch (error) {
                Logger.error(`[Renderer] Error loading resolved material at slot ${targetIndex}:`, error);
            }
            // Detached while the load was in flight (reload) — stop before touching
            // the slot map onDetach cleared (also covers a throw that straddled detach).
            if (outcome === 'detached' || this.attachedNode === null) return false;
            // A concurrent commit re-owned the slot (or its mesh vanished) mid-fetch:
            // applying this stale mat's properties would clobber the winner, so skip.
            // An ordinary `failed` still paints authored props over the existing
            // material — the pre-race behavior MaterialLoadOutcome.failed documents.
            if (outcome === 'superseded') return false;
            this.applyMaterialProperties(targetIndex, this._liveMaterialForSlot(targetIndex) ?? mat);
            return true;
        }));
        if (this.attachedNode === null) return;
        const appliedAny = applied.includes(true);
        // Re-apply the whole-object material properties over every touched slot,
        // exactly as applyCommit does after a hot-swap. Skipping these dropped
        // authored opacity/castShadows on the (re)assigned material — the same
        // "reload mutates the material" bug class, for opacity instead of
        // roughness/metallic — and it must reach a late mesh even when its GLB
        // load FAILED, since onAttach's earlier opacity pass ran before it existed.
        if (appliedAny) {
            this._syncModelPins();
            this._applyCastShadows();
            this._applyOpacity();
            this._applyTint();
        }
    }

    /** Drop the ref a slot's assignment took on `src` (see {@link _assignAcquiredMaterial}). */
    private _releaseLoadedSrc(src: string): void {
        this._releaseMaterialContainer(src);
        const pathIdx = this._materialContainerPaths.indexOf(src);
        if (pathIdx !== -1) this._materialContainerPaths.splice(pathIdx, 1);
    }

    private _acquireMaterialContainer(src: string): Promise<AssetContainer> {
        return this.resources.getOrLoadContainer(src, this.scene, undefined, Renderer.MATERIAL_CONTAINER_OPTS);
    }

    private _releaseMaterialContainer(src: string): void {
        this.resources.releaseContainer(src, Renderer.MATERIAL_CONTAINER_OPTS);
    }

    /**
     * Prefetch material GLB containers during load phase (I/O only).
     * Stores containers for later use in onAttach().
     */
    private async prefetchMaterialContainers(): Promise<void> {
        Logger.debug(`[Renderer] Prefetching material containers for ${this.sceneObject.name}`);
        const materials = this._resolved.materials || [];

        // Every slot's GLB at once: a multi-material mesh (structure + accent +
        // surface) otherwise waits for each download before starting the next.
        await Promise.all(materials.map(async (materialData, index) => {
            // Skip unresolved bindings (e.g. provider not attached yet) — they
            // are not URLs. Resolution later triggers a hot-swap via applyCommit.
            if (!drivesValue(materialData.src)) return;
            try {
                Logger.debug(`[Renderer] Prefetching material from: ${materialData.src}`);
                const container = await this._acquireMaterialContainer(materialData.src);
                this._materialContainerPaths.push(materialData.src);
                this._prefetchedContainers.set(index, container);
            } catch (error) {
                Logger.error(`[Renderer] Error prefetching material at index ${index}:`, error);
            }
        }));

        await this._prefetchBoundMaterialVariants();
    }

    /**
     * Prefetch every material GLB a property-bound `src` can resolve to.
     *
     * When `src` is bound (`"$material"`), states on the providing Properties
     * behavior can swap the URL at runtime. Loading on first activation would
     * hitch mid-experience, so we warm the container cache with all declared
     * values (base + every authored state) up front. Containers are ref-counted;
     * the refs taken here are released on detach via _materialContainerPaths.
     */
    private async _prefetchBoundMaterialVariants(): Promise<void> {
        const urls = new Set<string>();
        for (const mat of this.getAuthored().materials || []) {
            for (const url of this._boundMaterialVariants(mat)) urls.add(url);
        }

        // The resolved base src is already prefetched (and ref-counted) by
        // prefetchMaterialContainers — don't acquire a second ref for it.
        const pending = [...urls].filter(url => !this._materialContainerPaths.includes(url));
        await Promise.allSettled(pending.map(async url => {
            try {
                await this._acquireMaterialContainer(url);
                this._materialContainerPaths.push(url);
            } catch (error) {
                Logger.warn(`[Renderer] Error prefetching bound material variant ${url}:`, error);
            }
        }));
    }

    /** Every material URL a bound `src` (`"$material"`) can resolve to: Base plus each state's value. */
    private _boundMaterialVariants(mat: MaterialData): string[] {
        const refMatch = mat.src?.match(WHOLE_REF_RE);
        if (!refMatch) return [];
        const name = wholeRefName(refMatch);
        const provider = findPropertyProvider(this.sceneObject, name);
        if (!provider) return [];
        return provider.getDeclaredValues(name).filter(
            (value): value is string => typeof value === 'string' && value !== '' && !isPropertyBinding(value),
        );
    }

    /**
     * Get every state-bound material ready to show without yielding: loaded
     * (values declared since load() included) and its shader compiled for the
     * slot's mesh. A posed thumbnail applies a state inside a synchronous window
     * and renders at once; a material that is still loading misses that window,
     * and one whose shader is still compiling is skipped by the render, so the
     * card would lose the state's look. Best-effort and bounded.
     */
    public async prepareBoundMaterialVariants(): Promise<void> {
        if (this.attachedNode === null) return;
        await this._prefetchBoundMaterialVariants();
        const compiles: Promise<void>[] = [];
        for (const mat of this.getAuthored().materials || []) {
            const mesh = this.getSlotMesh(mat.targetMaterialIndex ?? 0);
            if (!mesh) continue;
            for (const url of this._boundMaterialVariants(mat)) {
                const material = this.resources.peekLoadedContainer(url, Renderer.MATERIAL_CONTAINER_OPTS)?.materials[0];
                if (material) compiles.push(compileMaterialForMesh(material, mesh));
            }
        }
        await Promise.all(compiles);
    }

    /**
     * Assign prefetched material containers to their target meshes.
     * Called from onAttach() — meshes are guaranteed to exist.
     */
    private assignPrefetchedMaterials(): void {
        const materials = this._resolved.materials || [];

        for (const [index, materialData] of materials.entries()) {
            if (!materialData.src) continue;

            const container = this._prefetchedContainers.get(index);
            if (!container) continue;

            const targetIndex = materialData.targetMaterialIndex ?? 0;
            Logger.debug(`[Renderer] Assigning prefetched material at index ${index} to slot ${targetIndex}`);

            let targetMesh = this.getSlotMesh(targetIndex);
            if (!targetMesh) {
                Logger.error(`[Renderer] No mesh found at slot ${targetIndex}`);
                continue;
            }

            if (container.materials.length === 0) {
                Logger.warn(`[Renderer] No materials found in ${materialData.src}`);
                continue;
            }

            // Promote InstancedMesh to real Mesh so material assignment works
            targetMesh = this.ensureMaterialAssignable(targetMesh);

            // Clone the material so modifications don't affect other users of the same container.
            // Dispose any previous clone for this slot.
            const sourceMaterial = container.materials[0];
            const clonedMaterial = cloneMaterial(sourceMaterial, `${this.sceneObject.name}_mat${targetIndex}`);
            if (!clonedMaterial) {
                Logger.warn(`[Renderer] Failed to clone material from ${materialData.src}`);
                continue;
            }
            this._clonedMaterials.get(targetIndex)?.dispose();
            this._clonedMaterials.set(targetIndex, clonedMaterial);
            this._loadedSrcBySlot.set(targetIndex, materialData.src);
            targetMesh.material = clonedMaterial;
            Logger.debug(`[Renderer] Assigned cloned material ${clonedMaterial.name} to mesh at index ${targetIndex}`);
        }

        // Containers are consumed — clear the prefetch map
        this._prefetchedContainers.clear();
    }

    /**
     * Apply all material properties (color, UV scale) during onAttach phase.
     * Called after load() completes - textures are guaranteed ready.
     */
    private applyAllMaterialProperties(): void {
        const materials = this._materialsBySlotLastWins(this._resolved.materials || []);
        Logger.debug(`[Renderer] Applying properties to ${materials.length} materials`);

        for (const materialData of materials) {
            this.applyMaterialProperties(materialData.targetMaterialIndex ?? 0, materialData);
        }
    }

    /**
     * Load and assign a replacement material from GLB.
     * Used by commit() for hot-reload — meshes already exist at that point.
     * Uses ResourceManager's container cache so identical material GLBs are only fetched once.
     *
     * @returns a MaterialLoadOutcome (see the type). The container ref this takes
     * is balanced on EVERY path: released on any non-`assigned` return, tracked
     * in _materialContainerPaths (for release on detach) only on `assigned`.
     */
    private async loadAndAssignMaterial(index: number, materialData: MaterialData): Promise<MaterialLoadOutcome> {
        if (!materialData.src) {
            return 'failed';
        }

        const targetIndex = materialData.targetMaterialIndex ?? 0;
        Logger.debug(`[Renderer] Loading replacement material at index ${index} for slot ${targetIndex}`);

        // Cheap pre-flight: skip the I/O entirely if the slot has no mesh.
        if (!this.getSlotMesh(targetIndex)) {
            Logger.error(`[Renderer] No mesh found at slot ${targetIndex}`);
            return 'failed';
        }

        Logger.debug(`[Renderer] Loading material from: ${materialData.src}`);
        // Own the fetch failure here so BOTH load callers see a plain outcome and
        // apply authored props uniformly — no caller-side catch can drift on
        // whether a thrown fetch (404/CORS/network) counts as a failure, which is
        // exactly what MaterialLoadOutcome.failed documents. A rejected load
        // SELF-EVICTS from the ResourceManager cache (no ref survives it), so
        // there is nothing to release here and a later commit's retry genuinely
        // re-fetches — the recoverability `failed` promises. A reject that
        // coincides with a detach is treated as a detach (caller bails).
        let container: AssetContainer;
        try {
            container = await this._acquireMaterialContainer(materialData.src);
        } catch (error) {
            Logger.error(`[Renderer] Error loading material from ${materialData.src}:`, error);
            return this.attachedNode === null ? 'detached' : 'failed';
        }
        // The await is a suspension point. Three race conditions can make assigning
        // now wrong; each releases the ref we just took (onDetach's release loop
        // already ran in the detach case, so it never sees this src) and abandons:
        //   1. the renderer detached (reload) — caller must stop entirely;
        //   2. a newer commit re-resolved this slot to a different src — it wins;
        //   3. the Model re-created its meshes mid-load, disposing the one we'd
        //      have picked before the await (the case this fix is about, with the
        //      renderer still attached).
        if (this.attachedNode === null) {
            this._releaseMaterialContainer(materialData.src);
            return 'detached';
        }
        return this._assignAcquiredMaterial(targetIndex, materialData.src, container);
    }

    /**
     * Assign a material whose container has ALREADY loaded, synchronously —
     * null when it has not (the caller falls back to {@link loadAndAssignMaterial}).
     *
     * State-bound variants are prefetched at load() so a switch doesn't hitch,
     * yet an `await` on the settled container still deferred the swap past the
     * current synchronous call. A thumbnail's pose window (apply state → render
     * → restore, no yield) therefore always rendered the previous material, and
     * the card stored that under the state's hash for good.
     */
    private _assignLoadedMaterial(materialData: MaterialData): MaterialLoadOutcome | null {
        if (!materialData.src) return null;
        const container = this.resources.peekLoadedContainer(materialData.src, Renderer.MATERIAL_CONTAINER_OPTS);
        if (!container) return null;
        const targetIndex = materialData.targetMaterialIndex ?? 0;
        if (!this.getSlotMesh(targetIndex)) {
            Logger.error(`[Renderer] No mesh found at slot ${targetIndex}`);
            return 'failed';
        }
        // A cache hit: takes this slot's ref now, balanced exactly as the async path's.
        void this._acquireMaterialContainer(materialData.src);
        return this._assignAcquiredMaterial(targetIndex, materialData.src, container);
    }

    /**
     * Clone `container`'s material onto the slot's live mesh. The caller holds
     * one container ref for `src`; it is kept on `assigned`, where it replaces
     * the ref the slot's previous material held, and released on every other
     * outcome.
     */
    private _assignAcquiredMaterial(targetIndex: number, src: string, container: AssetContainer): MaterialLoadOutcome {
        if (this._liveMaterialForSlot(targetIndex)?.src !== src) {
            this._releaseMaterialContainer(src);
            return 'superseded';
        }
        // Re-resolve the mesh after any await — getSlotMesh is self-healing (live
        // query + pin backfill), so a mesh re-created during the load is picked up
        // and the material lands on the LIVE mesh, not a disposed one. getSlotMesh
        // never returns a disposed mesh, so a miss here means the slot lost its
        // mesh mid-load — a race, not an ordinary failure.
        const liveMesh = this.getSlotMesh(targetIndex);
        if (!liveMesh) {
            this._releaseMaterialContainer(src);
            return 'superseded';
        }

        if (container.materials.length === 0) {
            Logger.warn(`[Renderer] No materials found in ${src}`);
            this._releaseMaterialContainer(src);
            return 'failed';
        }

        // Clone the material so modifications don't affect other users of the same container.
        const sourceMaterial = container.materials[0];
        const clonedMaterial = cloneMaterial(sourceMaterial, `${this.sceneObject.name}_mat${targetIndex}`);
        if (!clonedMaterial) {
            Logger.warn(`[Renderer] Failed to clone material from ${src}`);
            this._releaseMaterialContainer(src);
            return 'failed';
        }

        // Committed to assigning: promote InstancedMesh → Mesh now, so the
        // promotion is always paired with an assignment (no stray promotion on an
        // abandoned load), and record the pre-clone material for restore.
        const targetMesh = this.ensureMaterialAssignable(liveMesh);
        this._captureOriginalIfAbsent(targetIndex, targetMesh);

        // Dispose any previous clone for this slot.
        const previousClone = this._clonedMaterials.get(targetIndex);
        if (previousClone) {
            previousClone.dispose();
            // Drop the tint capture keyed by the disposed clone — without this,
            // repeated state-driven src swaps grow _originalAlbedo with
            // disposed-material keys and _restoreTint writes to dead materials.
            this._originalAlbedo.delete(previousClone);
            this._originalPbrFactors.delete(previousClone);
            this._authoredAlbedo.delete(previousClone);
        }
        const previousSrc = this._loadedSrcBySlot.get(targetIndex);
        this._materialContainerPaths.push(src);
        this._clonedMaterials.set(targetIndex, clonedMaterial);
        this._loadedSrcBySlot.set(targetIndex, src);
        if (previousSrc !== undefined) this._releaseLoadedSrc(previousSrc);
        targetMesh.material = clonedMaterial;
        Logger.debug(`[Renderer] Assigned cloned material ${clonedMaterial.name} to mesh at index ${targetIndex}`);
        return 'assigned';
    }

    /**
     * Helper to get the meshes THIS object owns (child SceneObjects' meshes
     * excluded — a parent's slots/opacity/tint must never reach them).
     * Delegates to getOwnedMeshes() for consistent ordering with MaterialPreviews.
     */
    private getMeshes(): AbstractMesh[] {
        return this.sceneObject.getOwnedMeshes();
    }

    /**
     * Return the mesh for a slot index, or undefined if none exists.
     *
     * Prefers the stable map captured at onAttach() (kept consistent through
     * InstancedMesh promotions). When the pin is missing or disposed — on reload
     * the Model can (re)create meshes just after onAttach captured the map — it
     * recovers positionally from the live scene graph and backfills the pin.
     *
     * That positional lookup (`getMeshes()[slotIndex]`) is only sound while the
     * list still matches capture order. Once meshes are removed/disposed the list
     * shifts, so the candidate at `slotIndex` may actually belong to a DIFFERENT
     * slot — or be a non-model helper (a collider picker, a selection box) that
     * shares this transform. Guard against both:
     *   - never hand back a mesh already pinned to another slot (it would
     *     cross-assign that slot's material and corrupt the pin map);
     *   - never NEWLY pin a helper mesh (a styled GLB material must not land on
     *     the invisible collider box). Helpers present at capture stay pinned as
     *     they always were — this only stops the fallback from adopting one.
     * Decline instead, leaving the caller on the safe "no mesh at slot" path.
     *
     * Also declines entirely once detached: onDetach clears _meshSlots, and a
     * stray post-detach call (an in-flight commit, a late property change) must
     * not resurrect the map by backfilling from the still-alive transform.
     */
    private getSlotMesh(slotIndex: number): AbstractMesh | undefined {
        if (this.attachedNode === null) return undefined;
        const pinned = this._meshSlots.get(slotIndex);
        if (pinned && !pinned.isDisposed()) return pinned;
        const candidate = this.getMeshes()[slotIndex];
        if (!candidate || candidate.isDisposed()) return undefined;
        if (Renderer._isHelperMesh(candidate)) return undefined;
        for (const [key, mesh] of this._meshSlots) {
            if (mesh === candidate && key !== slotIndex) return undefined;
        }
        this._meshSlots.set(slotIndex, candidate);
        return candidate;
    }

    /**
     * A non-model helper mesh that shares this object's transform — a collider
     * picker (`__collider`), a selection box (`__selection`), or a Babylon
     * internal (`__`-prefixed). Slot materials must never be assigned to these.
     * Mirrors ContactShadow._shouldSkip's name-based exclusion, the codebase's
     * established convention for telling helper meshes from renderable ones.
     */
    private static _isHelperMesh(mesh: AbstractMesh): boolean {
        const name = mesh.name;
        return name.startsWith('__')
            || name.endsWith(COLLIDER_MESH_SUFFIX)
            || name.endsWith(SELECTION_MESH_SUFFIX);
    }

    /**
     * The live resolved material entry for a slot, LAST-WINS when several entries
     * target the same slot (serialize() documents "later entries override earlier
     * ones"). Read from `_resolved` at call time so a value a concurrent commit
     * wrote while we awaited a load is the one we act on — not a pre-await copy.
     */
    private _liveMaterialForSlot(targetIndex: number): MaterialData | undefined {
        let found: MaterialData | undefined;
        for (const m of this._resolved.materials ?? []) {
            if ((m.targetMaterialIndex ?? 0) === targetIndex) found = m;
        }
        return found;
    }

    /**
     * Materials collapsed to one entry per slot, last-wins — the same rule
     * serialize() applies. Deduping up front keeps a duplicate `[A@0, B@0]` from
     * loading A only to have the slot's own supersede guard abandon it, and stops
     * the redundant releaseContainer that repeated per-commit load churn causes.
     */
    private _materialsBySlotLastWins(materials: MaterialData[]): MaterialData[] {
        const bySlot = new Map<number, MaterialData>();
        for (const m of materials) bySlot.set(m.targetMaterialIndex ?? 0, m);
        return [...bySlot.values()];
    }

    /**
     * True if `mat` is a clone this behavior owns — used to avoid recording one
     * of our own clones as a slot's "original" material.
     */
    private _isOwnClone(mat: Material): boolean {
        for (const clone of this._clonedMaterials.values()) {
            if (clone === mat) return true;
        }
        return false;
    }

    /**
     * Record a slot's pre-clone material name so a later restore can put it back.
     * onAttach's captureOriginalMaterials seeds this for meshes that existed then;
     * a mesh created AFTER capture (the reload race) is only ever pinned by the
     * getSlotMesh fallback, so without this it has no recorded original — and
     * restoreOriginalMaterial would then dispose our clone while reassigning
     * nothing, leaving the mesh rendering default gray. Seed only when absent and
     * when the current material isn't already one of our clones.
     */
    private _captureOriginalIfAbsent(slotIndex: number, mesh: AbstractMesh): void {
        if (this._originalMaterials[slotIndex] !== undefined) return;
        const mat = mesh.material;
        if (!mat || this._isOwnClone(mat)) return;
        this._originalMaterials[slotIndex] = mat.name;
    }

    /**
     * Backfill pins for model slots whose mesh appeared after capture, so the
     * whole-object appliers (opacity/tint/castShadows, which iterate _meshSlots)
     * reach late-built sibling primitives too — not only the one slot a material
     * entry happened to load. Uses getSlotMesh, which declines helper meshes and
     * cross-pinned candidates, so this never adopts a foreign mesh.
     */
    private _syncModelPins(): void {
        const count = this.getMeshes().length;
        for (let i = 0; i < count; i++) this.getSlotMesh(i);
    }

    /**
     * Apply castShadows metadata to all child meshes.
     * ContactShadow checks this flag to exclude meshes from the shadow render.
     */
    private _applyCastShadows(): void {
        const cast = this._resolved.castShadows !== false;
        for (const mesh of this._meshSlots.values()) {
            if (!mesh.metadata) mesh.metadata = {};
            (mesh.metadata as Record<string, unknown>).castShadows = cast;
        }
    }

    /**
     * The whole-object opacity, clamped to [0, 1]; 1 when unset. Clamped
     * because an eased bound opacity can overshoot mid-flight with
     * overshooting easings (back, custom beziers) — alpha must not.
     */
    private _wholeOpacity(): number {
        const o = this._resolved.opacity;
        return typeof o === 'number' ? Math.max(0, Math.min(1, o)) : 1;
    }

    /**
     * A slot entry's own opacity in [0, 1], or null when it carries none.
     * Presence, not truthiness: 0 is a real value (the slot is hidden). A bound
     * value that has not resolved is a string here and drives nothing, the way
     * an unresolved factor binding does.
     */
    private static _slotOpacityOf(entry: MaterialData | undefined): number | null {
        const o = entry?.opacity;
        return typeof o === 'number' && Number.isFinite(o) ? Math.max(0, Math.min(1, o)) : null;
    }

    /**
     * What multiplies into a slot's alpha: the whole-object opacity times the
     * slot's own (1 for a slot that has none).
     */
    private _driveOpacity(slotIndex: number): number {
        const slotOpacity = this._clonedMaterials.has(slotIndex)
            ? Renderer._slotOpacityOf(this._liveMaterialForSlot(slotIndex))
            : null;
        return this._wholeOpacity() * (slotOpacity ?? 1);
    }

    /**
     * Apply opacity to every slot: the whole-object `opacity` times the slot's
     * own `MaterialData.opacity`, times the colour alpha where a `#RRGGBBAA`
     * colour owns the slot.
     * Manages the opaque ↔ alpha-blend transparency mode transition so that
     * fully-opaque objects stay in the fast render path.
     *
     * Below 1 it drives the slot, multiplying into a colour-carried alpha
     * rather than replacing it. At 1 it restores only the slots it made
     * transparent itself: a slot a colour owns keeps the colour's alpha, and
     * a material that is translucent on its own — GLB-authored glass — is
     * left alone. That last rule cannot be "unless opacity was authored":
     * every loaded Renderer carries the schema default `opacity: 1`.
     *
     * A slot's own opacity is written to the slot's own clone of the material
     * (applyMaterialProperties makes one for it), so the object's other slots
     * and other placements of the model, which share the glTF material,
     * keep theirs. The whole-object factor writes to whatever the mesh wears.
     */
    private _applyOpacity(): void {
        for (const [slotIndex, mesh] of this._meshSlots) {
            const mat = mesh.material;
            if (!mat) continue;

            // Opacity is a no-op on an occluder, and not just cosmetically:
            // alpha-blend would move it out of the sorted opaque queue that
            // guarantees it draws before the geometry it hides.
            if (isOccluderMaterial(mat)) {
                this._opacitySlots.delete(slotIndex);
                continue;
            }

            const opacity = this._driveOpacity(slotIndex);
            const drives = opacity < 1;

            const colorAlpha = this._colorAlpha.get(slotIndex) ?? Renderer._ownColorAlpha(mesh);
            if (colorAlpha !== undefined) {
                // The pre-pass follows the COMBINED alpha: a colour-owned slot
                // faded to 0 by the opacity must stop writing depth exactly as
                // a directly authored 0 does.
                mat.alpha = colorAlpha * opacity;
                mat.needDepthPrePass = mat.alpha > 0;
                continue;
            }

            if (!drives) {
                if (!this._opacitySlots.delete(slotIndex)) continue;
                this._restoreAuthoredTransparency(slotIndex, mat);
                continue;
            }

            this._captureAuthoredTransparency(slotIndex, mat);
            mat.alpha = opacity;
            // Switch to alpha-blend so the GPU actually composites; the depth
            // pre-pass avoids common self-sorting artifacts. It follows the
            // combined alpha, as the colour-owned branch above does: a slot
            // faded to 0 is invisible and must not occlude what it reveals.
            mat.transparencyMode = Material.MATERIAL_ALPHABLEND;
            mat.needDepthPrePass = mat.alpha > 0;
            this._opacitySlots.add(slotIndex);
        }
    }

    /**
     * Undo a color-carried alpha: restore full opacity and the captured
     * transparency mode. A no-op for a slot no colour owns — a material whose
     * SOURCE is natively transparent (cloned for a UV override, colour never
     * authored) keeps its own alpha. While the opacity — whole-object or the
     * slot's own — is driving (< 1) the slot is handed to that pass instead,
     * which runs after every applyMaterialProperties call and takes it over.
     */
    private _clearColorAlpha(mat: Material, slotIndex: number): void {
        if (!this._colorAlpha.delete(slotIndex)) return;
        if (this._driveOpacity(slotIndex) < 1) return;
        this._restoreAuthoredTransparency(slotIndex, mat);
    }

    /**
     * Remember a slot's authored alpha and transparency mode the first time
     * either transparency pass touches it. Preserve a `null` mode explicitly —
     * GaussianSplattingMaterial leaves it null so the material decides
     * per-render; collapsing null to MATERIAL_OPAQUE breaks splat alpha
     * blending and renders the raw quads without Gaussian falloff.
     */
    private _captureAuthoredTransparency(slotIndex: number, mat: Material): void {
        // An editor preview's copy carries that preview's values, not the
        // file's: nothing to remember as authored.
        if (transparencyOwnedElsewhere(mat)) return;
        // On the material too, for anything that copies it while it is driven.
        // Stamped on every fade start, not only the first: a restore takes the
        // stamp off while this slot keeps its saved values, so the next fade
        // must put it back before another placement reads the live fields.
        recordAuthoredTransparency(mat);
        if (this._originalTransparencyModes.has(slotIndex)) return;
        // Read back from that record: when another placement already drives
        // this shared material, its live fields are the fade, not the file.
        const authored = authoredTransparencyOf(mat);
        this._originalTransparencyModes.set(slotIndex, authored.transparencyMode);
        this._originalAlphas.set(slotIndex, authored.alpha);
    }

    /**
     * Hand a slot back the alpha and mode it had before any pass touched it,
     * whichever other overrides still hold its clone.
     */
    private _restoreAuthoredTransparency(slotIndex: number, mat: Material): void {
        // A preview's copy goes back to what its owner gave it, not to
        // anything recorded as authored.
        const owned = transparencyOwnedElsewhere(mat);
        if (owned) {
            mat.alpha = owned.alpha;
            mat.transparencyMode = owned.transparencyMode;
            mat.needDepthPrePass = owned.needDepthPrePass;
            return;
        }
        mat.alpha = this._originalAlphas.get(slotIndex) ?? 1;
        const original = this._originalTransparencyModes.get(slotIndex);
        if (original !== undefined) mat.transparencyMode = original;
        mat.needDepthPrePass = false;
        // Its fields are authored again; the stamp goes with the drive.
        clearAuthoredTransparency(mat);
    }

    /**
     * Apply the whole-model tint: multiply every slot's authored albedo by
     * the tint color. Reaches GLB-authored textured materials on all slots —
     * albedoColor multiplies over the albedo texture — unlike the per-slot
     * material color, which replaces one slot's color. Clearing the tint
     * restores the captured albedos exactly.
     */
    private _applyTint(): void {
        const raw = typeof this._resolved.tint === 'string' ? this._resolved.tint.trim() : '';
        let tint: Color3 | null = null;
        if (raw) {
            // validate() only guards AUTHORED literals — a property binding
            // ("$accent") can resolve to anything at runtime, so the resolved
            // value must be re-checked here. Skip (keeping the previous tint)
            // rather than applying the black Color4.FromHexString fallback.
            if (!Renderer.TINT_HEX_RE.test(raw)) {
                Logger.warn(`[Renderer] Ignoring invalid tint "${raw}" — expected #RRGGBB or #RRGGBBAA hex`);
                return;
            }
            const c = Color4.FromHexString(raw);
            tint = new Color3(c.r, c.g, c.b); // alpha (if present) intentionally dropped
        }

        // One write per distinct material — a shared material must not be
        // multiplied once per slot that references it.
        const written = new Set<Material>();
        for (const mesh of this._meshSlots.values()) {
            const mat = mesh.material;
            if (!mat || written.has(mat)) continue;
            written.add(mat);
            // Tint is a no-op on an occluder: invisible in play mode, and the
            // editor shows the plain black fallback.
            if (isOccluderMaterial(mat)) continue;

            const albedo = (mat as PBRMaterial).albedoColor;
            if (!albedo) continue; // non-PBR material — nothing to tint

            // Capture the authored albedo on first touch of this material;
            // replaced/cloned/restored materials are new keys and capture
            // fresh, so the multiply base stays the authored color.
            let base = this._originalAlbedo.get(mat);
            if (!base) {
                base = albedo.clone();
                this._originalAlbedo.set(mat, base);
            }

            (mat as PBRMaterial).albedoColor = tint
                ? base.multiply(tint)
                : base.clone();
        }
    }

    /** Restore every tint-captured albedo (used on detach). */
    private _restoreTint(): void {
        for (const [material, color] of this._originalAlbedo) {
            (material as PBRMaterial).albedoColor = color.clone();
        }
        this._originalAlbedo.clear();
    }

    /**
     * Ensure a mesh supports material assignment.
     * InstancedMesh ignores .material — promote it to a real Mesh clone
     * of its source so per-object material overrides work.
     * Returns the mesh unchanged if it's already a regular Mesh.
     */
    private ensureMaterialAssignable(mesh: AbstractMesh): AbstractMesh {
        const cloned = promoteInstanceToMesh(mesh);
        if (cloned === mesh) return mesh;

        // Keep _meshSlots consistent: replace the now-disposed instance with the clone.
        // The break is load-bearing: .set() during Map iteration is safe in JS, but only
        // because we exit immediately. Each InstancedMesh maps to exactly one slot.
        for (const [slotIndex, slotMesh] of this._meshSlots) {
            if (slotMesh === mesh) {
                this._meshSlots.set(slotIndex, cloned);
                break;
            }
        }

        return cloned;
    }

    /**
     * Capture original material names before any replacements are applied.
     * Stores mesh index → material name mapping in _originalMaterials.
     * This metadata is used by editor utilities (MaterialPreviews) for slot detection.
     */
    private captureOriginalMaterials(): void {
        const meshes = this.getMeshes();
        this._originalMaterials = {};
        this._meshSlots = new Map();

        meshes.forEach((mesh, meshIndex) => {
            this._meshSlots.set(meshIndex, mesh);
            if (mesh.material) {
                this._originalMaterials[meshIndex] = mesh.material.name;
            }
        });

        Logger.debug(`[Renderer] Captured ${Object.keys(this._originalMaterials).length} original materials for ${this.sceneObject.name}`);
    }

    /**
     * Get original material names captured before any replacements were applied.
     * Used by editor utilities (MaterialPreviews) for slot detection.
     *
     * @returns Record mapping mesh index to original material name
     */
    public getOriginalMaterials(): Record<number, string> {
        return this._originalMaterials;
    }

    /**
     * Get the pinned mesh slot map: slotIndex → AbstractMesh.
     * Captured at onAttach() and maintained through InstancedMesh promotions.
     * Used by editor utilities (MaterialPreviews) for stable mesh lookup.
     */
    public getMeshSlots(): ReadonlyMap<number, AbstractMesh> {
        return this._meshSlots;
    }

    /**
     * Re-apply the object's opacity and castShadows over the meshes it owns
     * now. Runs when the object announces its meshes changed, for a behavior
     * that builds or rebuilds a mesh after this renderer attached.
     */
    public refreshMeshes(): void {
        if (this.attachedNode === null) return;
        this._syncModelPins();
        this._applyCastShadows();
        this._applyOpacity();
    }

    /**
     * The alpha a mesh's own color carries, when its builder recorded one in
     * `metadata.colorAlpha`. Opacity multiplies into it, as into a `#RRGGBBAA`
     * material color, and the builder keeps the mesh in the blend mode.
     */
    private static _ownColorAlpha(mesh: AbstractMesh): number | undefined {
        const alpha = (mesh.metadata as { colorAlpha?: unknown } | null)?.colorAlpha;
        return typeof alpha === 'number' && alpha < 1 ? alpha : undefined;
    }

    /**
     * The albedo authored by the REPLACEMENT material this slot wears, as hex,
     * or null when the slot wears no replacement.
     *
     * Editors need this because `_originalMaterials` records the material the
     * mesh wore *before* replacement — a colour the replacement never had.
     *
     * Gated on `_loadedSrcBySlot`, which is set only once a replacement GLB has
     * actually loaded and been assigned. That is the authoritative signal: an
     * authored `src` may be a `$name` binding that resolved to a URL (a
     * replacement IS worn) or to nothing (it is not), and inspecting the
     * authored string cannot tell those apart.
     *
     * Reads through the capture maps before the live value, because the live
     * albedo is derived whenever this behavior has driven it: a tint has
     * already multiplied it, a `baseColorFactor` override has replaced it
     * outright. No capture means the colour was never driven — exactly when the
     * live value IS the authored one.
     *
     * @returns hex, or null if no replacement / no mesh / no base colour
     */
    public getReplacementAlbedoHex(slotIndex: number): string | null {
        if (!this._loadedSrcBySlot.has(slotIndex)) return null;
        const mat = this.getSlotMesh(slotIndex)?.material;
        if (!mat) return null;
        const captured = this._authoredAlbedo.get(mat) ?? this._originalAlbedo.get(mat);
        if (captured) return captured.toHexString();
        // Replacements are glTF materials, so PBR — matching how every other
        // colour path in this class reads a material.
        const albedo = (mat as PBRMaterial).albedoColor;
        return albedo ? albedo.toHexString() : null;
    }

    /**
     * The material the slot wears at Base, which Default shows: the one its Base
     * `src` names, else the mesh's own. It is not the live one while a state
     * swaps the slot's material. Null when it isn't loaded or isn't PBR.
     */
    public getBaseSlotMaterial(slotIndex: number): PBRMaterial | null {
        const baseSrc = readAtBase(() => this._resolveAll()).materials
            ?.find(material => (material.targetMaterialIndex ?? 0) === slotIndex)?.src ?? '';
        const material = baseSrc === (this._loadedSrcBySlot.get(slotIndex) ?? '')
            ? this.getSlotMesh(slotIndex)?.material
            : baseSrc
                ? this.resources.peekLoadedContainer(baseSrc, Renderer.MATERIAL_CONTAINER_OPTS)?.materials[0]
                : this.scene.getMaterialByName(this._originalMaterials[slotIndex] ?? '');
        return material instanceof PBRMaterial ? material : null;
    }

    /**
     * Restore the original material for a mesh slot, dispose our clone, and
     * release the container ref its loaded `src` held.
     */
    private restoreOriginalMaterial(slotIndex: number): void {
        const cloned = this._clonedMaterials.get(slotIndex);
        if (!cloned) return;

        const mesh = this.getSlotMesh(slotIndex);
        const originalName = this._originalMaterials[slotIndex];
        if (mesh && originalName) {
            const material = this.scene.getMaterialByName(originalName);
            if (material) {
                mesh.material = material;
            } else {
                // Original material was unloaded/disposed — null the mesh material rather than
                // leaving it pointing at the clone we're about to dispose.
                Logger.warn(`[Renderer] Original material "${originalName}" not found in scene for slot ${slotIndex}`);
                mesh.material = null;
            }
        }
        cloned.dispose();
        this._clonedMaterials.delete(slotIndex);
        const loadedSrc = this._loadedSrcBySlot.get(slotIndex);
        if (loadedSrc !== undefined) {
            this._loadedSrcBySlot.delete(slotIndex);
            this._releaseLoadedSrc(loadedSrc);
        }
        // Clear the slot's transparency bookkeeping so nothing written for the
        // clone lands on the restored material — which is container-shared:
        // a stale colour alpha here made _applyOpacity write alpha + a depth
        // pre-pass onto every instance of the GLB, and a later opaque re-add
        // read an original mode that was never captured.
        this._originalTransparencyModes.delete(slotIndex);
        this._originalAlphas.delete(slotIndex);
        this._colorAlpha.delete(slotIndex);
        this._opacitySlots.delete(slotIndex);
        this._originalAlbedo.delete(cloned); // the clone is gone; the restored material captures fresh if new
        this._originalPbrFactors.delete(cloned); // ditto — a Map key holds the disposed Material strongly
        this._authoredAlbedo.delete(cloned); // ditto
        Logger.debug(`[Renderer] Restored original material for slot ${slotIndex}`);
    }

    /**
     * Apply color and UV properties to a material.
     * Clones the material first if it's still shared with the container,
     * so modifications don't bleed to other objects using the same model.
     * Called from onAttach() - textures are guaranteed to be loaded.
     */
    private applyMaterialProperties(targetIndex: number, materialData: MaterialData): void {
        let targetMesh = this.getSlotMesh(targetIndex);

        if (!targetMesh || !targetMesh.material) {
            return;
        }

        // The color only drives when it resolved to a usable hex — see
        // _drivingColor. An unresolved binding is NOT a color, and treating it
        // as one is how a slot whose style stopped declaring `<role>Color`
        // renders transparent black instead of its authored albedo.
        const color = drivingHexColor(materialData.baseColorFactor, 'Renderer.baseColorFactor');
        const hasColor = color !== null;
        const hasUv = !!(materialData.texture_transform?.scale);
        // Numeric factors are checked by presence, never truthiness: 0 is a
        // meaningful override (mirror-smooth / fully dielectric). A bound factor
        // carries the binding STRING in a field typed `number`, so this gate
        // reads an unresolved factor binding as absent already, and the restore
        // below hands the authored value back.
        const factors = Renderer._factorChannelsOf(materialData);
        const hasRoughness = factors.roughness;
        const hasMetallic = factors.metallic;
        // Same reading as baseColorFactor: an unresolved binding is not a
        // colour, so drivingHexColor gates it out and the restore below hands
        // the authored emissive back.
        const emissive = drivingHexColor(materialData.emissiveFactor, 'Renderer.emissiveFactor');
        const hasEmissive = emissive !== null;
        // The slot's own opacity drives only below 1 (an authored 1 is the
        // same as none), and then it is an override like the others: the alpha
        // must land on a material this slot owns, because the glTF material is
        // shared with the object's other slots and with every other placement.
        const slotOpacity = Renderer._slotOpacityOf(materialData);
        const hasOpacity = slotOpacity !== null && slotOpacity < 1;

        // Nothing to override — restore original material if we had a clone
        if (!hasColor && !hasUv && !hasRoughness && !hasMetallic && !hasEmissive && !hasOpacity && !drivesValue(materialData.src)) {
            this.restoreOriginalMaterial(targetIndex);
            return;
        }

        // Ensure we have our own material clone for this slot.
        // First call clones from the shared material; subsequent calls reuse the existing clone.
        let mat = this._clonedMaterials.get(targetIndex);
        if (mat && targetMesh.material !== mat) {
            // The mesh stopped wearing our clone: the editor's projection
            // preview (or a state swap) interposed its own object-private
            // clone above it, recording ours as the material to hand back on
            // restore. Adopt the swap — write this commit into the material
            // the mesh actually renders, or every color/factor/UV edit
            // mutates a material nobody sees. Seed the worn clone's authored
            // captures from ours so withdrawing an override still restores
            // authored values, not the override the interposed clone was
            // born carrying. The cached clone stays the slot's restore
            // target.
            const worn = targetMesh.material; // non-null — guarded on entry
            const authoredAlbedo = this._authoredAlbedo.get(mat);
            if (authoredAlbedo && !this._authoredAlbedo.has(worn)) {
                this._authoredAlbedo.set(worn, authoredAlbedo.clone());
            }
            const authoredFactors = this._originalPbrFactors.get(mat);
            if (authoredFactors && !this._originalPbrFactors.has(worn)) {
                this._originalPbrFactors.set(worn, { ...authoredFactors });
            }
            this._seedAuthoredUvScale(mat, worn);
            mat = worn;
        }
        if (!mat) {
            // Promote InstancedMesh to real Mesh so material assignment works
            targetMesh = this.ensureMaterialAssignable(targetMesh);

            const shared = targetMesh.material!;
            // Record the pre-clone material so restore has something to put back,
            // even for a slot whose mesh was pinned late by the getSlotMesh fallback.
            this._captureOriginalIfAbsent(targetIndex, targetMesh);
            const cloned = cloneMaterial(shared, `${this.sceneObject.name}_mat${targetIndex}`);
            if (!cloned) {
                Logger.warn(`[Renderer] Failed to clone material for property override at slot ${targetIndex}`);
                return;
            }
            // The clone copied the shared material's LIVE albedo, which is
            // tinted while a tint is active. Reset it to the captured authored
            // base so the clone never enters _applyTint carrying a tinted
            // value as its "authored" color (tint²).
            const sharedBase = this._originalAlbedo.get(shared);
            if (sharedBase) {
                (cloned as PBRMaterial).albedoColor = sharedBase.clone();
            }
            // NOTE: if the shared material carried an editor projection
            // preview, Babylon's clone() recreated its plugin with default
            // (disabled) state. That is fine here: the editor preview layer
            // (src/editor) observes material swaps on the slots it projected
            // — the assignment below fires that observer — and re-projects
            // this clone in place on the following microtask. The runtime
            // deliberately does nothing about materials' plugins.
            this._clonedMaterials.set(targetIndex, cloned);
            targetMesh.material = cloned;
            mat = cloned;
        }

        // An occluder honours none of the per-slot overrides (design doc
        // §1.1): colour and factors are never visible in play mode and would
        // only muddy the black fallback in the editor, and an alpha byte (or a
        // slot opacity) would move it out of the sorted opaque queue it needs
        // to occlude at all.
        if (isOccluderMaterial(mat)) {
            this._colorAlpha.delete(targetIndex);
            this._opacitySlots.delete(targetIndex);
            return;
        }

        // Capture this material's authored albedo before the first override,
        // exactly as the PBR factors are captured below. Prefer the tint's
        // captured base when there is one: while a tint is active the live
        // albedoColor is already multiplied, and capturing that would make a
        // later clear restore a tinted color as though it were authored.
        const pbrForCapture = mat as PBRMaterial;
        if (hasColor && pbrForCapture.albedoColor && !this._authoredAlbedo.has(mat)) {
            const authored = this._originalAlbedo.get(mat) ?? pbrForCapture.albedoColor;
            this._authoredAlbedo.set(mat, authored.clone());
        }

        // Apply or restore base color. `albedoWritten` gates the tint-capture
        // invalidation below: the capture is only stale if we actually moved
        // the base color, and dropping it when we did not makes _applyTint
        // re-capture the LIVE (already tinted) value as the authored base —
        // clearing the tint would then restore a once-tinted color.
        let albedoWritten = false;
        if (hasColor) {
            try {
                const parsed = Color4.FromHexString(color);
                // Authored hex is sRGB (what a designer or model picks by eye);
                // albedoColor is a LINEAR slot. Writing sRGB bytes straight in
                // rendered every override ~1.5 stops too bright (#808080 came
                // out at 221, rust-red as salmon — bench swatch 2026-09-11).
                (mat as PBRMaterial).albedoColor = new Color3(parsed.r, parsed.g, parsed.b).toLinearSpace();
                albedoWritten = true;
                // An 8-digit hex carries alpha. Route it through the same
                // alpha-blend transition — and the same original-mode
                // bookkeeping — the opacity property uses. Parsing the alpha
                // and dropping it rendered every authored overlay (e.g. a
                // gradient built from #RRGGBBAA steps) fully opaque.
                if (parsed.a < 1) {
                    this._captureAuthoredTransparency(targetIndex, mat);
                    // The colour owns this slot's alpha from here: the
                    // opacity (whole-object and the slot's own) multiplies
                    // into it (_applyOpacity) and never restores it —
                    // clearing the colour does.
                    this._colorAlpha.set(targetIndex, parsed.a);
                    this._opacitySlots.delete(targetIndex);
                    mat.alpha = parsed.a * this._driveOpacity(targetIndex);
                    mat.transparencyMode = Material.MATERIAL_ALPHABLEND;
                    // A fully transparent slot must not write depth, or it
                    // occludes exactly what it is meant to reveal: a kit
                    // frame's open aperture hid everything behind it. Decided
                    // on the combined alpha, so a whole-object fade to 0
                    // releases the depth write too (_applyOpacity re-derives
                    // it on every pass).
                    mat.needDepthPrePass = mat.alpha > 0;
                } else {
                    this._clearColorAlpha(mat, targetIndex);
                }
            } catch (error) {
                Logger.error(`[Renderer] Failed to parse color ${materialData.baseColorFactor}:`, error);
            }
        } else {
            // Color stopped driving — restore THIS material's authored albedo,
            // captured above before the first override. Reading it off
            // `_originalMaterials[slot]` instead (as this did) is only right
            // for a native slot: on a slot carrying a replacement GLB it hands
            // back the pre-replacement mesh material's color, which the
            // replacement never had.
            //
            // A missing capture means this behavior never overrode the color,
            // so there is nothing to put back — a clone created this call for a
            // uv/factor override was already reset to the authored base when it
            // was cloned.
            const authored = this._authoredAlbedo.get(mat);
            if (authored) {
                (mat as PBRMaterial).albedoColor = authored.clone();
                albedoWritten = true;
            }
            this._clearColorAlpha(mat, targetIndex);
        }
        // The material's base color changed — the tint capture is stale.
        // Dropping it makes _applyTint (which runs after this in onAttach/
        // applyCommit) re-capture, so the tint multiplies over the new color.
        // Only when we actually wrote: see `albedoWritten`.
        if (albedoWritten) this._originalAlbedo.delete(mat);

        // Apply or restore PBR factors. Captured lazily, before the first
        // override, so a later clear restores the value this material actually
        // authored — for a slot carrying a replacement GLB that is the
        // replacement's value, not the original mesh material's.
        const pbrMat = mat as PBRMaterial;
        if ((hasRoughness || hasMetallic || hasEmissive) && !this._originalPbrFactors.has(mat)) {
            this._originalPbrFactors.set(mat, {
                roughness: pbrMat.roughness,
                metallic: pbrMat.metallic,
                emissive: pbrMat.emissiveColor.clone(),
            });
        }
        const authoredFactors = this._originalPbrFactors.get(mat);
        if (hasRoughness) {
            pbrMat.roughness = materialData.roughnessFactor!;
        } else if (authoredFactors) {
            pbrMat.roughness = authoredFactors.roughness;
        }
        if (hasMetallic) {
            pbrMat.metallic = materialData.metallicFactor!;
        } else if (authoredFactors) {
            pbrMat.metallic = authoredFactors.metallic;
        }
        this._applyFactorTexture(pbrMat, factors);
        // Emissive is a colour, not a factor pair, but it restores on exactly
        // the same rule: withdraw the override and the material's authored
        // glow comes back. The alpha byte of an #RRGGBBAA is ignored — glow
        // has no transparency; `opacity` owns that.
        if (hasEmissive) {
            pbrMat.emissiveColor = Color3.FromHexString(emissive.slice(0, 7)).toLinearSpace();
        } else if (authoredFactors) {
            pbrMat.emissiveColor = authoredFactors.emissive.clone();
        }

        // Apply or restore UV scale. Without a `texture_transform` the slot
        // keeps whatever tiling the file authored.
        this.applyUvScale(mat, hasUv ? materialData.texture_transform!.scale : null);
    }

    /** The same maps of a copy carry the authored tiling `from` recorded, whatever the copy was born wearing. */
    private _seedAuthoredUvScale(from: Material, to: Material): void {
        for (const texProp of Renderer.TEXTURE_PROPERTIES) {
            const source: unknown = from[texProp as keyof Material];
            const copy: unknown = to[texProp as keyof Material];
            if (!(source instanceof Texture) || !(copy instanceof Texture)) continue;
            const authored = this._authoredUvScale.get(source);
            if (authored && !this._authoredUvScale.has(copy)) this._authoredUvScale.set(copy, authored);
        }
    }

    /**
     * Apply UV scale directly to material textures, or with `null` hand back
     * the tiling each texture had before this behavior first wrote one — a
     * texture it never wrote is left as it is.
     * Called from onAttach() - textures are guaranteed loaded.
     */
    private applyUvScale(mat: Material, scale: readonly [number, number] | null): void {
        const pbrMat = mat as PBRMaterial;
        for (const texProp of Renderer.TEXTURE_PROPERTIES) {
            const texture: unknown = pbrMat[texProp as keyof PBRMaterial];
            if (!(texture instanceof Texture)) continue;
            if (scale) {
                if (!this._authoredUvScale.has(texture)) this._authoredUvScale.set(texture, [texture.uScale, texture.vScale]);
                [texture.uScale, texture.vScale] = scale;
                continue;
            }
            const authored = this._authoredUvScale.get(texture);
            if (!authored) continue;
            [texture.uScale, texture.vScale] = authored;
            this._authoredUvScale.delete(texture);
        }
    }

    /**
     * A factor override replaces the authored value, but the PBR shaders
     * multiply the factor by the metallic-roughness texture (B metalness,
     * G roughness), so on a texture authored at 0 the override shows
     * nothing. While a factor drives, the material wears a copy of that
     * texture with the overridden channels at 1; the exporter writes the
     * texture the material wears, so the published file renders the same.
     * Withdrawing the override puts the authored texture back.
     *
     * A copy needs an async readback. The first apply starts it and changes
     * nothing; its arrival applies the copy to the material each slot wears
     * then, which may be one the editor projection interposed meanwhile. A
     * source still loading is not read: the factors apply again once it has
     * loaded.
     */
    private _applyFactorTexture(mat: PBRMaterial, channels: FactorChannels): void {
        const mask = `${channels.metallic ? "m" : ""}${channels.roughness ? "r" : ""}`;
        const current = this._factorTextures.get(mat);
        if (current?.mask === mask) return;
        if (current) this._withdrawFactorTexture(mat);

        const wearing = mat.metallicTexture;
        if (!(wearing instanceof Texture)) return;
        let authored: Texture = wearing;
        const copySource = this._factorCopySources.get(authored.getInternalTexture()?.uniqueId ?? -1);
        if (copySource) {
            // A clone of a material that wore a copy (the editor projection
            // clones what the mesh wears) inherits the copy as if authored.
            // Put the copy's source back so withdrawing restores the real one.
            const restored = copySource.clone();
            copyFactorTextureSettings(authored, restored);
            mat.onDisposeObservable.addOnce(() => restored.dispose());
            mat.metallicTexture = restored;
            authored = restored;
        }
        const gpu = authored.getInternalTexture();
        if (!mask || !gpu) return;
        if (!authored.isReady()) {
            this._applyFactorTexturesWhenLoaded(authored);
            return;
        }
        const cacheKey = `${gpu.uniqueId}:${mask}`;
        const derived = this._derivedFactorTextures.get(cacheKey);
        if (derived === undefined) {
            this._derivedFactorTextures.set(cacheKey, null);
            const source = authored.clone();
            const name = authored.name;
            void this._trackMaterialWork(deriveFactorTexture(authored, channels).then(
                (texture) => this._storeFactorTexture(cacheKey, source, texture),
                (error: unknown) => {
                    source.dispose();
                    Logger.warn(`[Renderer] Reading "${name}" back failed; its factor override keeps multiplying: ${String(error)}`);
                },
            ));
            return;
        }
        if (!derived) return;

        // Keep the authored sampling while replacing only the selected channels.
        const worn = derived.clone();
        worn.name = derived.name;
        copyFactorTextureSettings(authored, worn);
        const onDispose = mat.onDisposeObservable.addOnce(() => this._withdrawFactorTexture(mat));
        this._factorTextures.set(mat, { mask, authored, worn, onDispose });
        mat.metallicTexture = worn;
    }

    private _storeFactorTexture(cacheKey: string, source: Texture, texture: Texture | null): void {
        const gpu = texture?.getInternalTexture();
        if (!texture || !gpu || this.attachedNode === null || this._derivedFactorTextures.get(cacheKey) !== null) {
            texture?.dispose();
            source.dispose();
            return;
        }
        this._derivedFactorTextures.set(cacheKey, texture);
        this._factorCopySources.set(gpu.uniqueId, source);
        this._applyFactorTexturesToWornMaterials();
    }

    private _applyFactorTexturesWhenLoaded(texture: Texture): void {
        if (this._factorLoadWaits.has(texture)) return;
        const observer = texture.onLoadObservable.addOnce(() => {
            this._factorLoadWaits.delete(texture);
            this._applyFactorTexturesToWornMaterials();
        });
        this._factorLoadWaits.set(texture, observer);
    }

    /** Only where applyMaterialProperties writes: a slot with its own clone,
     *  or the material interposed over it, never a shared glTF material. */
    private _applyFactorTexturesToWornMaterials(): void {
        for (const materialData of this._materialsBySlotLastWins(this._resolved.materials || [])) {
            const slotIndex = materialData.targetMaterialIndex ?? 0;
            if (!this._clonedMaterials.has(slotIndex)) continue;
            const mat = this.getSlotMesh(slotIndex)?.material;
            if (!(mat instanceof PBRMaterial) || isOccluderMaterial(mat)) continue;
            this._applyFactorTexture(mat, Renderer._factorChannelsOf(materialData));
        }
    }

    /** A factor drives when it is a number: presence, never truthiness, since
     *  0 is a real override. A bound factor carries its binding STRING. */
    private static _factorChannelsOf(materialData: MaterialData): FactorChannels {
        return {
            metallic: typeof materialData.metallicFactor === 'number',
            roughness: typeof materialData.roughnessFactor === 'number',
        };
    }

    private _withdrawFactorTexture(mat: Material): void {
        const state = this._factorTextures.get(mat);
        if (!state) return;
        this._factorTextures.delete(mat);
        mat.onDisposeObservable.remove(state.onDispose);
        const pbr = mat as PBRMaterial;
        if (pbr.metallicTexture === state.worn) pbr.metallicTexture = state.authored;
        state.worn.dispose();
    }

    private _releaseFactorTextures(): void {
        for (const [texture, observer] of this._factorLoadWaits) {
            texture.onLoadObservable.remove(observer);
        }
        this._factorLoadWaits.clear();
        for (const mat of [...this._factorTextures.keys()]) {
            this._withdrawFactorTexture(mat);
        }
        for (const texture of this._derivedFactorTextures.values()) {
            texture?.dispose();
        }
        this._derivedFactorTextures.clear();
        for (const source of this._factorCopySources.values()) {
            source.dispose();
        }
        this._factorCopySources.clear();
    }

    public update(): void {
        // Renderer behavior doesn't need per-frame updates
    }

    protected onDetach(): void {
        Logger.debug(`[Renderer] Detaching renderer for ${this.sceneObject.name}`);

        this.sceneObject.onOwnedMeshesChangedObservable.remove(this._meshesObserver);
        this._meshesObserver = null;

        // Restore tinted albedos before material restore
        this._restoreTint();

        // Restore original opacity/transparency modes before material restore
        for (const slotIndex of this._originalTransparencyModes.keys()) {
            const mesh = this._meshSlots.get(slotIndex);
            if (mesh?.material) this._restoreAuthoredTransparency(slotIndex, mesh.material);
        }

        this._releaseFactorTextures();

        // Restore original materials before disposing clones.
        // restoreOriginalMaterial() uses _meshSlots, so clear must come AFTER this loop.
        for (const slotIndex of [...this._clonedMaterials.keys()]) {
            this.restoreOriginalMaterial(slotIndex);
        }
        this._clonedMaterials.clear(); // defensive: catch any entries restoreOriginalMaterial missed
        this._loadedSrcBySlot.clear();
        // A mesh that carries its own color alpha goes back to it, in the blend
        // mode its builder keeps it in. No pass above captured it, and a stale
        // capture would hand back an opaque mode. This runs after the original
        // materials are back: a slot override cloned the faded material, and
        // the original it swaps in is the one left on the mesh.
        for (const mesh of this._meshSlots.values()) {
            const alpha = Renderer._ownColorAlpha(mesh);
            if (alpha === undefined || !mesh.material) continue;
            mesh.material.alpha = alpha;
            mesh.material.transparencyMode = Material.MATERIAL_ALPHABLEND;
            mesh.material.needDepthPrePass = false;
        }

        // Release container references for material GLBs
        for (const path of this._materialContainerPaths) {
            this._releaseMaterialContainer(path);
        }
        this._materialContainerPaths = [];

        // Release pinned mesh references (after restores complete)
        this._meshSlots.clear();
        this._originalTransparencyModes.clear();
        this._originalAlphas.clear();
        this._colorAlpha.clear();
        this._opacitySlots.clear();
    }

    public dispose(): void {
        Logger.debug(`[Renderer] Disposing renderer for ${this.sceneObject.name}`);
    }

    /**
     * Validate renderer data structure
     */
    public validate(data: RendererData): ValidationError[] {
        const errors: ValidationError[] = [];

        // Validate @type
        if (data["@type"] !== "Renderer") {
            errors.push({
                field: "@type",
                message: `Expected "@type" to be "Renderer", got "${String(data["@type"])}"`
            });
        }

        if (data.pickThrough !== undefined && typeof data.pickThrough !== 'boolean'
            && !(typeof data.pickThrough === 'string' && isPropertyBinding(data.pickThrough))) {
            errors.push({ field: "pickThrough", message: "pickThrough must be a boolean or property binding" });
        }

        // Validate materials array
        if (data.materials) {
            if (!Array.isArray(data.materials)) {
                errors.push({
                    field: "materials",
                    message: "materials must be an array"
                });
            } else {
                data.materials.forEach((material, index) => {
                    if (material.src && typeof material.src !== 'string') {
                        errors.push({
                            field: `materials[${index}].src`,
                            message: "src must be a string"
                        });
                    }
                    if (material.targetMaterialIndex !== undefined && typeof material.targetMaterialIndex !== 'number') {
                        errors.push({
                            field: `materials[${index}].targetMaterialIndex`,
                            message: "targetMaterialIndex must be a number"
                        });
                    }
                    // Like the whole-object opacity, only a literal is checked:
                    // a property binding ("$fade") resolves later.
                    if (typeof material.opacity === 'number' && !(material.opacity >= 0 && material.opacity <= 1)) {
                        errors.push({
                            field: `materials[${index}].opacity`,
                            message: "opacity must be between 0 and 1"
                        });
                    }
                });
            }
        }

        // Validate opacity
        if (data.opacity !== undefined && typeof data.opacity === 'number') {
            if (data.opacity < 0 || data.opacity > 1) {
                errors.push({
                    field: "opacity",
                    message: "opacity must be between 0 and 1"
                });
            }
        }

        // Validate tint (property bindings like "$accent" resolve later — skip them)
        if (typeof data.tint === 'string' && data.tint !== ''
            && !isPropertyBinding(data.tint)
            && !Renderer.TINT_HEX_RE.test(data.tint)) {
            errors.push({
                field: "tint",
                message: "tint must be a #RRGGBB or #RRGGBBAA hex color (alpha is ignored)"
            });
        }

        return errors;
    }

    /**
     * Show the materials this object opens with — its bound slots, colour,
     * opacity and tint read at Base ({@link readAtBase}) — for a bake that has
     * to write the file's look, not the state on screen. The material
     * counterpart of `MorphStretch.showBaseSizeForBake`: a state that swaps a
     * slot's material is live on the mesh, and the bake serializes live
     * materials. Returns the way back to the session's view, or null when the
     * two already agree (no binding, or Base showing).
     *
     * Both directions run the ordinary {@link applyCommit}, so Base looks
     * exactly as it does when Base is selected: `''` puts the native material
     * back, a Base URL loads from the container load() prefetched. No state is
     * switched, so nothing listening for state changes hears it.
     *
     * If posing fails, the session's materials are put back before the error
     * propagates: a caller that gets a throw has no way back to call.
     */
    async showBaseMaterialsForBake(deadline = Date.now() + MATERIAL_SETTLE_TIMEOUT_MS): Promise<(() => Promise<void>) | null> {
        // A style that just landed has already moved `_resolved`, but its
        // material is still loading: comparing against it alone would bake the
        // material the mesh wore before.
        await this._settleMaterialWork(deadline);
        const base = readAtBase(() => this._resolveAll());
        if (JSON.stringify(base) === JSON.stringify(this._resolved)) return null;
        const showLive = async (): Promise<void> => {
            this._resolved = this._resolveAll();
            await this.applyCommit(this._resolved, this.getAuthored());
        };
        this._resolved = base;
        try {
            await this.applyCommit(base, this.getAuthored());
        } catch (error) {
            await showLive();
            throw error;
        }
        // Base's commit can start a factor-texture readback of its own.
        await this._settleMaterialWork(deadline);
        return showLive;
    }

    protected applyCommit(patch: Partial<RendererData>, prev: RendererData): Promise<void> {
        return this._trackMaterialWork(this._applyRendererCommit(patch, prev));
    }

    private async _applyRendererCommit(_patch: Partial<RendererData>, prev: RendererData): Promise<void> {
        Logger.debug(`[Renderer] Applying commit for ${this.sceneObject.name}`);

        // A property change fires applyCommit fire-and-forget (not awaited), so a
        // commit can arrive after onDetach cleared the slot map. Bail rather than
        // resurrect it — the object is being torn down or reloaded.
        if (this.attachedNode === null) return;

        const pickThrough = this._resolved.pickThrough === true;
        if (pickThrough !== this._lastPickThrough) {
            this._lastPickThrough = pickThrough;
            this.sceneObject.traceScene.refreshMeshPickability?.(this.sceneObject);
        }

        // Last-wins per slot: duplicate targetMaterialIndex entries collapse to
        // the later one (serialize()'s documented rule), so the loop below can't
        // load an earlier duplicate only for the slot's supersede guard to abandon it.
        const newMaterials = this._materialsBySlotLastWins(this._resolved.materials || []);
        const oldMaterials = prev.materials || [];

        // Map old slots by targetMaterialIndex for slot-membership diffing.
        // NOTE: `prev` is AUTHORED data (it may hold bindings like "$material"),
        // so it is only safe for membership checks — never compare field
        // values against it. The src diff baseline is _loadedSrcBySlot.
        const oldByIndex = new Map(oldMaterials.map(m => [m.targetMaterialIndex ?? 0, m]));
        const newByIndex = new Map(newMaterials.map(m => [m.targetMaterialIndex ?? 0, m]));

        // Restore original materials for slots that were removed entirely
        for (const slotIndex of oldByIndex.keys()) {
            if (!newByIndex.has(slotIndex)) {
                this.restoreOriginalMaterial(slotIndex);
            }
        }

        // Restore slots whose GLB replacement stopped driving — the src was
        // removed, a bound src resolved to `''` ("use the native material"), or
        // a bound src stopped resolving at all because the style that declared
        // the role is gone. The last case is the one a `!newMat.src` gate
        // missed: an unresolved binding is a non-empty string, so the slot kept
        // rendering the previous style's GLB with nothing left to say so.
        for (const slotIndex of [...this._loadedSrcBySlot.keys()]) {
            const newMat = newByIndex.get(slotIndex);
            if (newMat && !drivesValue(newMat.src)) this.restoreOriginalMaterial(slotIndex);
        }

        // Load changed GLB sources and apply all material properties. Slots are
        // distinct after last-wins, so every slot's load runs side by side: a
        // style swap on a mesh with three styled slots costs one download, not
        // three in a row.
        const outcomes = await Promise.all(newMaterials.map(async (mat, index): Promise<MaterialLoadOutcome | null> => {
            const targetIndex = mat.targetMaterialIndex ?? 0;
            const loadedSrc = this._loadedSrcBySlot.get(targetIndex);
            // Skip unresolved bindings — "$material" with no provider is not a URL
            const hasLoadableSrc = drivesValue(mat.src);
            let outcome: MaterialLoadOutcome | null = null;
            if (hasLoadableSrc && mat.src !== loadedSrc) {
                // The slot keeps wearing, and recording, the previous material
                // until the replacement is assigned, so a commit that lands
                // while this loads can still restore the slot.
                outcome = 'failed';
                try {
                    // A loaded container assigns without yielding, so a state
                    // switch shows its material within the same call.
                    outcome = this._assignLoadedMaterial(mat) ?? await this.loadAndAssignMaterial(index, mat);
                } catch (error) {
                    Logger.error(`[Renderer] Error hot-reloading material at index ${index}:`, error);
                }
                // Detached mid-load (reload) — the slot map is cleared; the
                // caller stops once every slot has settled.
                if (outcome === 'detached') return outcome;
                // A newer commit re-owned the slot (src changed) or the mesh
                // vanished mid-fetch — skip so this stale mat's values don't
                // overwrite what the winner assigned. An ordinary `failed` falls
                // through and still paints authored props over the existing material.
                if (outcome === 'superseded') return outcome;
            }
            // Apply the LIVE resolved entry: a concurrent commit may have
            // re-resolved this slot (a same-src property change) while we awaited a
            // load, and its values — not this pre-await copy's — must win.
            this.applyMaterialProperties(targetIndex, this._liveMaterialForSlot(targetIndex) ?? mat);
            return outcome;
        }));
        if (outcomes.includes('detached') || this.attachedNode === null) return;

        this._syncModelPins();
        this._applyCastShadows();
        this._applyOpacity();
        this._applyTint();
    }

    /**
     * Serialize behavior data for export.
     * Only includes materials that have actual changes from defaults.
     * For materials with only property changes (no src), excludes the src field.
     * Deduplicates materials by targetMaterialIndex, keeping the latest values.
     */
    public serialize(): RendererData {
        const materials = this.getAuthored().materials || [];

        // Map to deduplicate by targetMaterialIndex and merge properties
        // Later entries override earlier ones for the same index
        const materialMap = new Map<number, MaterialData>();

        for (const materialData of materials) {
            const targetIndex = materialData.targetMaterialIndex ?? 0;

            // Check if material has any changes from default
            const hasSrc = materialData.src && materialData.src.trim() !== '';
            const hasColorChange = materialData.baseColorFactor;
            const hasUvChange = materialData.texture_transform &&
                materialData.texture_transform.scale &&
                (materialData.texture_transform.scale[0] !== 1 || materialData.texture_transform.scale[1] !== 1);
            // Presence, not truthiness — 0 is a meaningful factor and a `!!`
            // check here would silently drop it on every save.
            //
            // Presence of the FIELD, not of a number: a bound slot's factor
            // carries the binding string (`"$structureRoughness"`) in a field
            // typed `number`, the same shape `opacity` uses — and `opacity` is
            // serialized on `!== undefined` for exactly this reason. Gating on
            // `typeof === 'number'` here would keep the src/color bindings
            // (their gates are truthy on strings) and silently drop the factor
            // bindings, un-adopting half of every themed slot on save.
            const hasRoughnessChange = materialData.roughnessFactor !== undefined;
            const hasMetallicChange = materialData.metallicFactor !== undefined;
            // Truthy like baseColorFactor, not presence like the factors: an
            // emissive is a hex string, and an empty one says nothing.
            const hasEmissiveChange = materialData.emissiveFactor;
            // Presence, not truthiness — 0 hides the slot. A 1 is carried too,
            // so a later 1 overrides an earlier fade for the same slot; it is
            // left out only after duplicates are resolved (below).
            const hasOpacityChange = materialData.opacity !== undefined;

            // Only serialize materials that have at least one change
            if (hasSrc || hasColorChange || hasUvChange || hasRoughnessChange || hasMetallicChange || hasEmissiveChange || hasOpacityChange) {
                // Get existing material for this index or create new one
                const existingMaterial = materialMap.get(targetIndex) || {
                    targetMaterialIndex: targetIndex
                };

                // Merge properties: later entries override earlier ones
                // Only include src if it was actually set (material replacement)
                if (hasSrc) {
                    existingMaterial.src = materialData.src;
                }

                // Include color if changed (latest value wins)
                if (hasColorChange) {
                    existingMaterial.baseColorFactor = materialData.baseColorFactor;
                }

                // Include UV scale if changed (latest value wins)
                if (hasUvChange) {
                    existingMaterial.texture_transform = {
                        scale: materialData.texture_transform!.scale
                    };
                }

                // Include PBR factors if set (latest value wins)
                if (hasRoughnessChange) {
                    existingMaterial.roughnessFactor = materialData.roughnessFactor;
                }
                if (hasMetallicChange) {
                    existingMaterial.metallicFactor = materialData.metallicFactor;
                }
                if (hasEmissiveChange) {
                    existingMaterial.emissiveFactor = materialData.emissiveFactor;
                }

                // Include the slot's own opacity (latest value wins)
                if (hasOpacityChange) {
                    existingMaterial.opacity = materialData.opacity;
                }

                materialMap.set(targetIndex, existingMaterial);
            }
        }

        // A slot's resolved opacity of 1 is left out, the way the whole-object
        // opacity is: it says what an absent value does. A bound one carries
        // its binding string in the field (the factor precedent above), which
        // is not 1 and is kept. An entry that carried nothing else goes too.
        const serializedMaterials = Array.from(materialMap.values()).filter(entry => {
            if (entry.opacity === 1) delete entry.opacity;
            return Object.keys(entry).some(key => key !== 'targetMaterialIndex');
        });

        const serialized: RendererData = {
            "@type": "Renderer"
        };

        // Only include materials array if there are materials to serialize
        if (serializedMaterials.length > 0) {
            serialized.materials = serializedMaterials;
        }

        // Include castShadows if it was set
        if (this.getAuthored().castShadows !== undefined) {
            serialized.castShadows = this.getAuthored().castShadows;
        }

        // Include opacity if non-default
        const authored = this.getAuthored();
        if (authored.pickThrough !== undefined) serialized.pickThrough = authored.pickThrough;
        if (authored.opacity !== undefined && authored.opacity !== 1) {
            serialized.opacity = authored.opacity;
        }

        // Include tint if present (empty string means "no tint" — omit it)
        if (authored.tint) {
            serialized.tint = authored.tint;
        }

        return serialized;
    }
}
